import React from 'react';

import { Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';

import {
  getMarketingRouterTreatmentConfig,
  marketingRouterTreatmentTypes,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import {
  ANYWHERE_REGION_FILTER,
  MARKETING_ROUTER_RULE_STATUS_OPTIONS,
  type MarketingRouterRuleFilters,
  getMarketingRouterRuleFilterChips,
  hasActiveMarketingRouterRuleFilters,
} from './marketingRouterRuleFilters';

type Props = {
  filters: MarketingRouterRuleFilters;
  onChange: (filters: MarketingRouterRuleFilters) => void;
  onReset: () => void;
  regionCodes: Array<string>;
  shownCount: number;
  totalCount: number;
};

/**
 * Search box, filter controls and the row of applied-filter chips for the
 * rules list. Holds no state of its own — the page owns the filters so they
 * can live in the URL.
 */
export default function MarketingRouterRulesFilterBar({
  filters,
  onChange,
  onReset,
  regionCodes,
  shownCount,
  totalCount,
}: Props) {
  const update = <Key extends keyof MarketingRouterRuleFilters>(key: Key, value: MarketingRouterRuleFilters[Key]) =>
    onChange({ ...filters, [key]: value });
  const chips = getMarketingRouterRuleFilterChips(filters);
  const hasFilters = hasActiveMarketingRouterRuleFilters(filters);

  return (
    <Stack spacing={1.5} sx={{ mb: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-end' }}>
        <TextField
          label="Search"
          placeholder="Rule name or id"
          size="small"
          value={filters.search}
          onChange={(event) => update('search', event.target.value)}
          sx={{ flex: '2 1 220px' }}
          inputProps={{ 'aria-label': 'Search rules by name or id' }}
        />
        <TextField
          label="Status"
          select
          size="small"
          value={filters.status}
          onChange={(event) => update('status', event.target.value as MarketingRouterRuleFilters['status'])}
          sx={{ flex: '1 1 140px' }}
        >
          <MenuItem value="">All</MenuItem>
          {MARKETING_ROUTER_RULE_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Active on"
          type="date"
          size="small"
          value={filters.activeOn}
          onChange={(event) => update('activeOn', event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 160px' }}
        />
        <TextField
          label="Region"
          select
          size="small"
          value={filters.region}
          onChange={(event) => update('region', event.target.value)}
          sx={{ flex: '1 1 140px' }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value={ANYWHERE_REGION_FILTER}>Anywhere only</MenuItem>
          {regionCodes.map((code) => (
            <MenuItem key={code} value={code}>
              {code}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Match contains"
          placeholder="e.g. google, brand_*"
          size="small"
          value={filters.match}
          onChange={(event) => update('match', event.target.value)}
          sx={{ flex: '1.5 1 180px' }}
          inputProps={{ 'aria-label': 'Filter by match value' }}
        />
        <TextField
          label="Treatment"
          select
          size="small"
          value={filters.treatment}
          onChange={(event) => update('treatment', event.target.value as MarketingRouterRuleFilters['treatment'])}
          sx={{ flex: '1 1 150px' }}
        >
          <MenuItem value="">All</MenuItem>
          {marketingRouterTreatmentTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {getMarketingRouterTreatmentConfig(type).typeName}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={onReset} disabled={!hasFilters} sx={{ flex: '0 0 auto' }}>
          Reset
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {chips.map((chip) => (
          <Chip
            key={chip.key}
            label={chip.label}
            size="small"
            color="primary"
            variant="outlined"
            onDelete={() => update(chip.key, '')}
          />
        ))}
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          Showing {shownCount} of {totalCount} {totalCount === 1 ? 'rule' : 'rules'}
        </Typography>
      </Stack>
    </Stack>
  );
}
